import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { createAppSocket } from '@/lib/socket';
import { type Socket } from 'socket.io-client';

interface WebSocketMessage {
  type: 'chat-message' | 'chat-notification' | 'room-update' | 'typing';
  data: any;
}

export const useWebSocket = () => {
  const { user } = useAuthStore();
  const socketRef = useRef<Socket | null>(null);
  const isConnectedRef = useRef(false);

  const connect = useCallback(() => {
    if (!user || socketRef.current?.connected) return;

    socketRef.current = createAppSocket({
      auth: {
        token: localStorage.getItem('token'),
      },
    });

    socketRef.current.on('connect', () => {
      console.log('WebSocket conectado');
      isConnectedRef.current = true;
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('WebSocket desconectado:', reason);
      isConnectedRef.current = false;
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('Erro na conexão WebSocket:', error);
      isConnectedRef.current = false;
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconectado após', attemptNumber, 'tentativas');
      isConnectedRef.current = true;
    });

    socketRef.current.on('reconnect_error', (error) => {
      console.error('Erro na reconexão WebSocket:', error);
    });

    socketRef.current.on('reconnect_failed', () => {
      console.error('Falha na reconexão WebSocket após todas as tentativas');
      isConnectedRef.current = false;
    });
  }, [user]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      isConnectedRef.current = false;
    }
  }, []);

  const joinRoom = useCallback((roomId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join-chat-room', { roomId });
    }
  }, []);

  const leaveRoom = useCallback((roomId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave-chat-room', { roomId });
    }
  }, []);

  const sendTyping = useCallback((roomId: string, isTyping: boolean) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing', { roomId, isTyping });
    }
  }, []);

  const onMessage = useCallback((callback: (message: WebSocketMessage) => void) => {
    if (socketRef.current) {
      socketRef.current.on('chat-message', (data) => {
        callback({ type: 'chat-message', data });
      });

      socketRef.current.on('chat-notification', (data) => {
        callback({ type: 'chat-notification', data });
      });

      socketRef.current.on('room-update', (data) => {
        callback({ type: 'room-update', data });
      });

      socketRef.current.on('typing', (data) => {
        callback({ type: 'typing', data });
      });
    }
  }, []);

  const isConnected = useCallback(() => {
    return isConnectedRef.current;
  }, []);

  useEffect(() => {
    if (user) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);

  return {
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    sendTyping,
    onMessage,
    isConnected,
  };
};
